import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Archive, Calendar, FileText, XCircle, CheckCircle, Search } from 'lucide-react';
import { cn } from '../lib/utils';

const FILTERS = [
  { key: 'all', label: 'Alle' },
  { key: 'completed', label: 'Erledigt' },
  { key: 'cancelled', label: 'Abgebrochen' },
];

export default function ArchivePage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const navigate = useNavigate();

  const loadOrders = useCallback((f) => {
    setLoading(true);
    api.get(`/orders/archived?filter=${f}`)
      .then(res => setOrders(res.data))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadOrders(filter); }, [filter, loadOrders]);

  const openPdf = async (id) => {
    try {
      const response = await api.get(`/orders/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast.error('PDF konnte nicht geladen werden');
    }
  };

  const companies = Array.from(new Set(orders.map(o => (o.monteur_company || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'de-CH'));
  const normalizedSearch = search.trim().toLowerCase();
  const visibleOrders = orders.filter(o => {
    if (companyFilter !== 'all' && (o.monteur_company || '').trim() !== companyFilter) return false;
    if (!normalizedSearch) return true;
    return [
      o.order_number, o.title, o.customer_name, o.property_address, o.object_name,
      o.renter_name, o.monteur_company, o.monteur_name,
    ].some(v => (v || '').toString().toLowerCase().includes(normalizedSearch));
  });

  const cancelReason = (order) => {
    const note = [...(order.internal_notes || [])].reverse().find(n => n.type === 'cancel' || (n.text || '').startsWith('Auftrag abgebrochen:'));
    return (note?.text || '').replace(/^Auftrag abgebrochen:?\s*/, '');
  };

  const subtitle = filter === 'cancelled'
    ? `${visibleOrders.length} von ${orders.length} abgebrochene Aufträge`
    : filter === 'completed'
    ? `${visibleOrders.length} von ${orders.length} erledigte Aufträge`
    : `${visibleOrders.length} von ${orders.length} archivierte Aufträge`;

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="archive-page">
        <div className="flex items-center gap-3 mb-6">
          <Archive className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
          <div>
            <h1 className="font-heading font-black text-3xl">Archiv</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-1 mb-5 bg-muted p-1 rounded-sm w-fit">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={cn(
                'px-4 py-1.5 text-sm rounded-sm transition-colors',
                filter === f.key ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[minmax(260px,1fr)_220px] gap-3 mb-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Archiv suchen..." className="pl-9 rounded-sm" data-testid="archive-search" />
          </div>
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="rounded-sm" data-testid="archive-company-filter"><SelectValue placeholder="Firma" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Firmen</SelectItem>
              {companies.map(company => <SelectItem key={company} value={company}>{company}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : visibleOrders.length === 0 ? (
          <div className="text-center py-16">
            <Archive className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" strokeWidth={1} />
            <p className="text-muted-foreground">Keine Aufträge</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {visibleOrders.map(o => {
              const reason = cancelReason(o);
              return (
              <div key={o.id} data-testid={`archive-row-${o.id}`} onClick={() => navigate(`/orders/${o.id}`)} className="flex items-center justify-between p-4 bg-card border border-border/60 rounded-sm hover:border-primary/30 transition-all stagger-item cursor-pointer">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-mono text-muted-foreground">{o.order_number}</span>
                    {o.cancelled ? (
                      <span className="flex items-center gap-1 text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-1.5 py-0.5 rounded-sm font-medium">
                        <XCircle className="w-3 h-3" />Abgebrochen
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-1.5 py-0.5 rounded-sm font-medium">
                        <CheckCircle className="w-3 h-3" />Erledigt
                      </span>
                    )}
                  </div>
                  <p className="font-medium text-sm text-foreground">{o.title}</p>
                  <div className="flex items-center gap-3 mt-1">
                    {o.customer_name && <span className="text-xs text-muted-foreground">{o.customer_name}</span>}
                    {o.property_address && <span className="text-xs text-muted-foreground">{o.property_address}</span>}
                    {o.monteur_company && <span className="text-xs text-muted-foreground">{o.monteur_company}</span>}
                    {o.archived_at && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" strokeWidth={1.5} />
                        {new Date(o.archived_at).toLocaleDateString('de-CH')}
                      </div>
                    )}
                  </div>
                  {o.cancelled && reason && <p className="text-xs text-red-700 dark:text-red-300 mt-1">Grund: {reason}</p>}
                </div>
                <div className="flex items-center gap-3 ml-4">
                  {o.totals?.total > 0 && (
                    <span className="text-sm font-mono font-medium text-foreground">CHF {o.totals.total.toFixed(2)}</span>
                  )}
                  {!o.cancelled && (
                    <button onClick={(e) => { e.stopPropagation(); openPdf(o.id); }} className="flex items-center gap-1.5 text-xs text-primary hover:underline" data-testid={`archive-pdf-${o.id}`}>
                      <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />PDF
                    </button>
                  )}
                </div>
              </div>
            );})}
          </div>
        )}
      </div>
    </Layout>
  );
}
